import { BILLING_MODELS } from '../../constants';
import { etapasContratadas } from '../../constants/schedule';

/**
 * QUANDO A RECEITA FOI GANHA — o eixo que faltava no financeiro.
 *
 * O sistema tinha quatro momentos para representar e representava dois. O
 * vencimento está na parcela, o caixa está no recebimento, o faturamento está
 * na fatura. A COMPETÊNCIA, que é quando o trabalho que gera a cobrança foi
 * concluído, não existia em lugar nenhum — embora o dado estivesse ali o tempo
 * todo, na data da TE, na da DG60, na da FIV.
 *
 * A consequência era que "quanto faturei em setembro" dependia de quem
 * lembrou de clicar. Nos sete serviços faturados até aqui, o intervalo entre o
 * trabalho fechar e a fatura sair foi de 7 a 17 dias. Os sete caíram no mesmo
 * mês por sorte: uma TE do dia 25 faturada no dia 12 leva a receita para o mês
 * seguinte, e nenhum relatório mostraria o deslocamento.
 *
 * Nada aqui grava coluna nova. A competência é DERIVADA do que o serviço já
 * sabe, e é assim que ela continua verdadeira quando alguém corrige a data de
 * uma etapa lançada errado.
 */

/** A etapa cuja conclusão dispara a cobrança, por modelo. */
const DATA_QUE_FECHA = {
  [BILLING_MODELS.POR_TE]:      (s) => (s.te_done ? s.te_date : null),
  [BILLING_MODELS.POR_PRENHEZ]: (s) => (s.pregnancy_60d_done ? s.pregnancy_60d_date : null),
  [BILLING_MODELS.POR_EMBRIAO]: (s) => (s.lab_done ? s.lab_date : null),
};

const dia = (v) => (v ? String(v).slice(0, 10) : null);

/**
 * A última etapa CONTRATADA que foi concluída.
 *
 * Serve ao pacote e ao personalizado, que só fecham quando tudo terminou: a
 * competência é o dia em que a última peça entrou. Usa a data REAL da etapa,
 * não a agendada — competência é sobre o que aconteceu.
 */
const ultimaEtapaConcluida = (service) => {
  const datas = etapasContratadas(service)
    .filter(e => e.done)
    .map(e => dia(service?.[e.actualField]))
    .filter(Boolean)
    .sort();
  return datas.length > 0 ? datas[datas.length - 1] : null;
};

/**
 * Em que dia este serviço passou a valer dinheiro. `null` enquanto não passou.
 *
 * `null` é resposta, não falha: serviço com a etapa em aberto ainda não tem
 * receita ganha, e forçar uma data ali seria inventar competência.
 *
 * A diária é o único modelo sem etapa: ela corre por dia trabalhado, e a data
 * do serviço é o que existe de mais próximo do fato gerador.
 *
 * Sem cobrança não tem competência NUNCA. Não é que a data falte — é que a
 * receita daquele trabalho pertence a outro serviço.
 */
export const dataDeCompetencia = (service) => {
  if (!service) return null;
  if (service.billing_model === BILLING_MODELS.SEM_COBRANCA) return null;

  const porModelo = DATA_QUE_FECHA[service.billing_model];
  if (porModelo) return dia(porModelo(service));

  if (service.billing_model === BILLING_MODELS.DIARIA) {
    return service.work_days ? dia(service.date) : null;
  }

  if (service.billing_model === BILLING_MODELS.PACOTE_COMPLETO
      || service.billing_model === BILLING_MODELS.PERSONALIZADO) {
    return ultimaEtapaConcluida(service);
  }

  return null;
};

/** A competência caiu dentro do período? Extremos inclusivos nos dois lados. */
export const noPeriodo = (data, { de, ate } = {}) => {
  if (!data) return false;
  if (de && data < dia(de)) return false;
  if (ate && data > dia(ate)) return false;
  return true;
};

/**
 * Os TRÊS LIVROS de um conjunto de serviços, no período.
 *
 * Eles respondem perguntas diferentes e por isso não batem entre si — e é
 * justamente a diferença que interessa:
 *
 *   `realizado` — trabalho que virou receita, faturado ou não. É "como foi o
 *                 mês", e o único que não depende de alguém ter clicado.
 *   `faturado`  — o que virou documento. Depende do clique, e por isso fica
 *                 atrás do realizado.
 *   `aFaturar`  — a diferença entre os dois. É dinheiro ganho que ninguém
 *                 cobrou, e hoje ele não aparece em cifra nenhuma no sistema.
 *
 * `valorDoServico` entra por parâmetro porque a conta mora em
 * `serviceValue.js` e depende do laboratório; trazer a dependência para cá
 * ligaria este módulo à tela inteira.
 */
export const tresLivros = (services = [], { de, ate, valorDoServico } = {}) => {
  const doPeriodo = (services || []).filter(s => noPeriodo(dataDeCompetencia(s), { de, ate }));

  const centavos = (n) => Math.round((Number(n) || 0) * 100);
  let realizado = 0;
  let faturado = 0;

  doPeriodo.forEach(s => {
    const valor = centavos(valorDoServico ? valorDoServico(s) : 0);
    realizado += valor;
    /*
     * O FATURADO É O MESMO PREÇO, contado só onde já existe documento.
     *
     * Lia `billed_value`, o valor congelado na emissão, e por isso "realizado"
     * e "faturado" podiam divergir por dois motivos misturados: ou faltava
     * emitir, ou o preço mudou depois de emitir. O segundo virava um buraco
     * silencioso no "a faturar", que é a diferença entre os dois.
     *
     * Com o cadastro ditando a conta, a diferença volta a ter um significado
     * só: o que já tem fatura e o que não tem.
     */
    if (s.billed) faturado += valor;
  });

  return {
    servicos: doPeriodo.length,
    realizado: realizado / 100,
    faturado: faturado / 100,
    aFaturar: (realizado - faturado) / 100,
    // Quem são, para a tela poder levar até eles em vez de só mostrar o total.
    naoFaturados: doPeriodo.filter(s => !s.billed),
  };
};
